import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-background p-6" data-testid="forgot-password-page">
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20"><Logo imgClassName="h-7" /></div>
      <div className="w-full max-w-sm">
        {sent ? (
          <div className="text-center flex flex-col items-center gap-3" data-testid="forgot-sent">
            <MailCheck className="h-12 w-12 text-green-600" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl font-bold tracking-tight">Check your inbox</h1>
            <p className="text-sm text-muted-foreground">If an account exists for <strong>{email}</strong>, we've sent a link to reset your password. The link expires in 60 minutes.</p>
            <Button asChild variant="outline" className="mt-4 w-full h-11" data-testid="forgot-back-login"><Link to="/login">Back to sign in</Link></Button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Forgot password?</h1>
            <p className="text-muted-foreground mt-2 text-sm">Enter your email and we'll send you a link to reset it.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-email-input" className="h-11" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="forgot-submit-button" className="w-full h-11 active:scale-[0.98] transition-transform">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground mt-8 text-center">
              Remembered it?{" "}
              <Link to="/login" className="text-accent font-medium hover:underline" data-testid="forgot-goto-login">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
